import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { NavigationError, RedirectCommand, Router } from '@angular/router';
import { createErrorHandler } from '@sentry/angular';
import { AppVersionService } from '../services/app-version.service';
import { CoreService } from '../services/core.service';
import { isChunkLoadError } from '../_utils/chunk-load-error';

/**
 * Router `withNavigationErrorHandler`: a navigation whose chunk failed reloads straight into its
 * target. A plain reload would land on the page the user was leaving, since the router only
 * updates the address bar once a navigation succeeds. While a reload would be too soon the error
 * is rethrown, so `ChunkReloadErrorHandler` tells the user instead of looping.
 */
export function reloadIntoFailedNavigation(
  navigationError: NavigationError,
): RedirectCommand | void {
  const appVersionService = inject(AppVersionService);

  if (!isChunkLoadError(navigationError.error) || appVersionService.isReloadThrottled()) {
    return;
  }

  void appVersionService.resetCacheAndReload(navigationError.url);

  // Settles the failed navigation in place while the page unloads; without a RedirectCommand the
  // router rethrows the error, and ChunkReloadErrorHandler would reload a second time.
  const router = inject(Router);

  return new RedirectCommand(router.parseUrl(router.url), { skipLocationChange: true });
}

@Injectable()
export class ChunkReloadErrorHandler extends ErrorHandler {
  private readonly appVersionService = inject(AppVersionService);
  // Resolved on use: CoreService depends on the router, which itself needs the ErrorHandler.
  private readonly injector = inject(Injector);
  // Console logging stays with Angular's handler (`super`); this one only reports to Sentry.
  private readonly sentryHandler = createErrorHandler({ logErrors: false });

  override handleError(error: unknown): void {
    if (this.appVersionService.isAbortedByReload(error)) {
      return;
    }

    if (!isChunkLoadError(error)) {
      this.report(error);
      return;
    }

    // A reload is already on its way: until it lands, more chunks of the replaced build fail.
    if (this.appVersionService.isReloading()) {
      return;
    }

    // Reloading again so soon would loop, so tell the user instead of dropping the error.
    if (this.appVersionService.isReloadThrottled()) {
      this.injector.get(CoreService).showError('errors.toastrErrors.chunkLoadFailed');
      this.report(error);
      return;
    }

    this.appVersionService.resetCacheAndReload();
  }

  private report(error: unknown): void {
    this.sentryHandler.handleError(error);
    super.handleError(error);
  }
}
